import { Button, Indicator } from 'is24-corecss'
import React, { useCallback, useMemo } from 'react'
import { ConvertedRealEstate } from '../../../../../../../../../@types/real-estate-api-types'
import { useTranslation } from 'react-i18next'
import styles from './RealEstateObject.module.css'
import { tracking } from '../../../../../../../../../tracking/tracking'
import { useAppSelector } from '../../../../../../../../../app/hooks'
import { InitState } from '../../../../../../../../../redux/reducers/root/initialState'

type RealEstateObjectProps = {
  object: ConvertedRealEstate
  onSelectObject: (object: ConvertedRealEstate) => void | Promise<void>
  isLoading?: boolean
}

export const RealEstateObject = (props: RealEstateObjectProps): React.JSX.Element => {
  const { t } = useTranslation()
  const { onSelectObject, isLoading = false } = props
  const { listing, meta } = props.object
  const flowInformation = useAppSelector((state: InitState) => state.flowFeature.flowInformation)

  const onCLick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e?.stopPropagation()
      tracking({
        eventName: 'select_unit_click',
        flowInformation,
        entryMode: 'create',
        experiment: 'object_chooser',
      })
      await onSelectObject(props.object)
    },
    [flowInformation, onSelectObject, props.object]
  )

  return (
    <Button
      variant='outlineStrong'
      align='start'
      className={'one-whole align-left padding-vertical-m palm-padding-horizontal-m font-body-medium-regular grid-item'}
      onClick={onCLick}
      data-testid={`unit-${meta.unitId ?? 'unknown'}`}
      disabled={isLoading}
    >
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className={'one-whole'}>
          <span className={'display-inline-block vertical-center'}>
            <ButtonContent object={props.object} />
          </span>
        </div>
        {listing.published && (
          <Indicator.Root appearance='label' color='white' variant='flat'>
            {t('objectChooser.modal.label.active')}
          </Indicator.Root>
        )}
        {!listing.published && meta.realEstateId && (
          <Indicator.Root appearance='label' color='white' variant='flat' className={`${styles.greyLabel}`}>
            {t('objectChooser.modal.label.draft')}
          </Indicator.Root>
        )}
      </div>
    </Button>
  )
}

function ButtonContent(props: { object: ConvertedRealEstate }) {
  const { object } = props
  const { listing } = object
  const { t } = useTranslation()

  const numberOfRooms = useMemo(
    () => (listing.room ? `${listing.room} ${t('objectChooser.abbreviation.room')}` : undefined),
    [listing.room, t]
  )

  const floor = useMemo(
    () => (listing.floor ? `${listing.floor}. ${t('objectChooser.abbreviation.floor')}` : undefined),
    [listing.floor, t]
  )

  const items = [
    {
      key: 'category',
      node: <span className='font-body-medium-bold'>{t(`objectChooser.type.${object.category}`)}</span>,
    },
    {
      key: 'livingSpace',
      node: <LivingSpace livingSpace={listing.livingSpace} />,
    },
    {
      key: 'numberOfRooms',
      node: <span className={'font-body-medium-regular'}>{numberOfRooms}</span>,
    },
    {
      key: 'floor',
      node: <span className={'font-body-medium-regular'}>{floor}</span>,
    },
  ]

  return (
    <span>
      {items
        .filter((item) => !!item.node)
        .map((item, index, array) => (
          <React.Fragment key={item.key}>
            {item.node} {index < array.length - 1 && <Pipe />}
          </React.Fragment>
        ))}
    </span>
  )
}

function LivingSpace(props: { livingSpace?: string }) {
  if (!props.livingSpace) {
    return undefined
  }
  return (
    <span className={'font-body-medium-regular'}>
      {props.livingSpace}m<sup>2</sup>
    </span>
  )
}

function Pipe() {
  return (
    <span style={{ color: 'lightgrey' }} className={'font-body-medium-regular'}>
      {' '}
      |{' '}
    </span>
  )
}
